/**
 * HelpArticleEditor — modal dialog for creating and editing Help articles.
 *
 * Props
 * -----
 * item     : existing HelpArticleOut | null   (null → create mode)
 * onSave   : (saved: HelpArticleOut) => void
 * onClose  : () => void
 */
import { useRef, useState } from 'react';
import { createHelpArticle, updateHelpArticle, uploadHelpAsset } from '../../services/api';
import MiniMarkdown from '../../utils/miniMarkdown';
import { HELP_PAGES, HELP_PANELS } from '../../utils/helpTaxonomy';
import styles from './HelpArticleEditor.module.css';

export default function HelpArticleEditor({ item, onSave, onClose }) {
  const isEdit = !!item;

  const [title, setTitle] = useState(item?.title ?? '');
  const [slug,  setSlug]  = useState(item?.slug  ?? '');
  const [page,  setPage]  = useState(item?.page  ?? '');
  const [panel, setPanel] = useState(item?.panel ?? '');
  const [tags,  setTags]  = useState(item?.tags  ?? '');
  const [order, setOrder] = useState(item?.order ?? 0);
  const [body,  setBody]  = useState(item?.body  ?? '');

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [uploading, setUploading] = useState(false);

  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);

  const insertAtCursor = (text) => {
    const el = bodyRef.current;
    if (!el) { setBody(b => b + text); return; }
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const next  = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again later
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { url } = await uploadHelpAsset(file);
      insertAtCursor(`![${file.name.replace(/\.[^.]+$/, '')}](${url})`);
    } catch (err) {
      setError(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !slug.trim() || !body.trim()) {
      setError('Title, slug, and body are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        slug: slug.trim(),
        body,
        page: page || null,
        panel: (page === 'sessions' && panel.trim()) ? panel.trim() : null,
        tags: tags.trim() || null,
        order: Number(order) || 0,
      };
      const saved = isEdit
        ? await updateHelpArticle(item.id, payload)
        : await createHelpArticle(payload);
      onSave(saved);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <span className={styles.title}>{isEdit ? 'Edit Help Article' : 'New Help Article'}</span>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          <div className={styles.row}>
            <label className={styles.field} style={{ flex: 2 }}>
              <span className={styles.fieldLabel}>Title</span>
              <input
                className={styles.input}
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Speed Trace"
                autoFocus
              />
            </label>
            <label className={styles.field} style={{ flex: 1 }}>
              <span className={styles.fieldLabel}>Slug</span>
              <input
                className={styles.input}
                value={slug}
                onChange={e => setSlug(e.target.value)}
                placeholder="e.g. panel.speedtrace"
              />
            </label>
            <label className={styles.field} style={{ flex: '0 0 70px' }}>
              <span className={styles.fieldLabel}>Order</span>
              <input
                type="number"
                className={styles.input}
                value={order}
                onChange={e => setOrder(e.target.value)}
              />
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Page</span>
              <select className={styles.input} value={page} onChange={e => { setPage(e.target.value); if (e.target.value !== 'sessions') setPanel(''); }}>
                {HELP_PAGES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
            {page === 'sessions' && (
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Panel</span>
                <select className={styles.input} value={panel} onChange={e => setPanel(e.target.value)}>
                  {HELP_PANELS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
            )}
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Tags (comma-separated)</span>
              <input
                className={styles.input}
                value={tags}
                onChange={e => setTags(e.target.value)}
                placeholder="e.g. speed,trace"
              />
            </label>
          </div>

          <div className={styles.previewRow}>
            <label className={styles.field}>
              <div className={styles.bodyLabelRow}>
                <span className={styles.fieldLabel}>Body (markdown)</span>
                <button
                  type="button"
                  className={styles.imageBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : '🖼 Insert Image'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp,image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleImagePick}
                  style={{ display: 'none' }}
                />
              </div>
              <textarea
                ref={bodyRef}
                className={styles.bodyInput}
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder={'## Heading\n\nExplain the feature. **Bold**, `code`, [links](https://…), ![alt text](https://… or /help-assets/foo.png), and bullet lists all work.'}
                rows={12}
                spellCheck={false}
              />
            </label>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Preview</span>
              <div className={styles.previewPane}>
                <MiniMarkdown text={body} />
              </div>
            </div>
          </div>

          {error && <div className={styles.errorMsg}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
